/**
 * "Belegungsübersicht" — the current status of the rooms IN OPERATION (Slice 18).
 *
 * CURRENT OPERATING TRUTH ONLY. Every number here comes from
 * `aggregateOccupancyForFilter`, i.e. from `GET /api/admin/occupancy` rows the
 * server already gated with `units_in_portfolio(units, day)`. Inventory counts
 * live in the KPI row's secondary lines and nowhere near this bar: a unit whose
 * lease has not started owns rooms, but not rooms that can be empty today.
 *
 * `operating === null` means the authoritative endpoint has not answered. That
 * is NOT zero occupancy — it is the absence of a measurement, and the card says
 * so instead of drawing an empty bar that reads as "nothing is let".
 *
 * ONLY CATEGORIES THE CONTRACT ACTUALLY CARRIES. The design reference also
 * shows an "In Vorbereitung" segment. No current-state source in this repo
 * produces it, so it is not here — a fourth legend row pinned at zero would be
 * a claim about a state nobody measures. "Reserviert" IS real: the occupancy
 * rows carry `reserved_rooms` and the page has aggregated it since Slice 7.
 */
import React from "react";

import { occupancyPercentText } from "../../../utils/occupancyPercent";

/** One legend line: dot, name, count. The count is text, never colour alone. */
function LegendRow({ dotClass, label, count }) {
  return (
    <li className="flex items-center gap-2 py-[3px]">
      <span className={`h-2 w-2 shrink-0 rounded-full ${dotClass}`} aria-hidden />
      <span className="flex-1 text-[12px] text-muted-foreground">{label}</span>
      <span className="font-mono text-[12px] font-medium text-foreground">{count}</span>
    </li>
  );
}

/**
 * @param {object|null} operating  `dashboardDisplay.operating`, or null when the
 *                                 occupancy endpoint has not answered.
 * @param {string}      unavailableNote  the page's shared "cannot say" copy.
 *
 * The percent formatter is no longer a prop: the occupancy rate goes through
 * the shared `occupancyPercentText` contract, which needs the denominator this
 * component already holds (OC3).
 */
export default function CoLivingOccupancyOverview({
  operating,
  unavailableNote,
}) {
  const total = operating ? operating.totalRooms : 0;
  const occupied = operating ? operating.occupiedRooms : 0;
  const reserved = operating ? operating.reservedRooms : 0;
  const free = operating ? operating.freeRooms : 0;

  // Widths come from the counts, not from the server's rounded percentages, so
  // the three segments cannot add up to more than the bar.
  const pct = (n) => (total > 0 ? (n / total) * 100 : 0);

  /*
   * OC3: the headline rate, which needs the denominator. `formatPercent` took
   * a value only, so a portfolio with no rooms recorded printed "0.0%" — the
   * same string a fully vacant portfolio prints, and the two are not the same
   * statement. The segment widths above keep using the counts.
   */
  const rateText = occupancyPercentText({
    total,
    rate: operating ? operating.occupiedRate : undefined,
  });

  return (
    <section
      aria-label="Belegungsübersicht"
      className="flex flex-col rounded-[10px] border border-border bg-card p-4"
    >
      <h2 className="m-0 text-[13px] font-semibold text-foreground">Belegungsübersicht</h2>
      <p className="m-0 mt-0.5 text-[11px] text-muted-foreground">
        Aktueller Status der Zimmer im Betrieb
      </p>

      {!operating ? (
        <p className="m-0 mt-4 text-[12px] leading-[1.5] text-muted-foreground">
          {unavailableNote}
        </p>
      ) : (
        <>
          <div className="mt-4 flex items-center gap-3">
            <div
              className="flex h-[10px] flex-1 overflow-hidden rounded-full bg-muted"
              role="img"
              aria-label={`Belegung ${rateText}: ${occupied} von ${total} Zimmern belegt, ${reserved} reserviert, ${free} frei`}
            >
              <div className="h-full bg-emerald-500" style={{ width: `${pct(occupied)}%` }} />
              <div className="h-full bg-amber-500" style={{ width: `${pct(reserved)}%` }} />
            </div>
            <span className="shrink-0 font-mono text-[15px] font-semibold text-foreground">
              {rateText}
            </span>
          </div>

          <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
            <span>{`${occupied} belegt`}</span>
            <span>{`${free} frei`}</span>
          </div>

          <ul className="m-0 mt-3 list-none border-t border-border p-0 pt-2">
            <LegendRow dotClass="bg-emerald-500" label="Belegt" count={occupied} />
            <LegendRow dotClass="bg-amber-500" label="Reserviert" count={reserved} />
            <LegendRow dotClass="bg-muted-foreground/40" label="Frei" count={free} />
          </ul>
        </>
      )}
    </section>
  );
}
